'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Movers, MoversQuerySessionType } from '@benzinga/movers-manager';
import { ErrorBoundary } from '@benzinga/core-ui';

import { MenuList, MoversBoard } from '../../../_components/markets';

const SESSIONS: { label: string; value: MoversQuerySessionType }[] = [
  { label: 'Regular', value: 'REGULAR' },
  { label: 'Premarket', value: 'PRE_MARKET' },
  { label: 'After Hours', value: 'AFTER_MARKET' },
];

interface MoversSessionBoardProps {
  initialMovers: Movers | null;
}

export function MoversSessionBoard({ initialMovers }: MoversSessionBoardProps) {
  const [session, setSession] = useState<MoversQuerySessionType>('REGULAR');
  const [movers, setMovers] = useState<Movers | null>(initialMovers);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef<AbortController | null>(null);
  const latestRequestRef = useRef(0);

  useEffect(() => () => inFlightRef.current?.abort(), []);

  const loadSession = useCallback(async (nextSession: MoversQuerySessionType) => {
    inFlightRef.current?.abort();
    const controller = new AbortController();
    const requestId = latestRequestRef.current + 1;
    inFlightRef.current = controller;
    latestRequestRef.current = requestId;

    setSession(nextSession);
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        maxResults: '24',
        moversQuery: 'volume_gt_1000000',
        session: nextSession,
      });
      const response = await fetch(`/api/movers?${params.toString()}`, { signal: controller.signal });
      if (!response.ok) {
        throw new Error('Failed to load movers');
      }
      const payload = await response.json();
      if (latestRequestRef.current !== requestId) return;
      const result = payload?.result as Movers | undefined;
      if (result) {
        result.all = [...(result.gainers ?? []), ...(result.losers ?? [])].sort(
          (a, b) => Math.abs(b.changePercent) - Math.abs(a.changePercent),
        );
      }
      setMovers(result ?? null);
    } catch (err) {
      if (latestRequestRef.current !== requestId) return;
      // Drop the previous session's rows so the board never contradicts the selected tab.
      setMovers(null);
      setError(err instanceof Error ? err.message : 'Failed to load movers');
    } finally {
      if (latestRequestRef.current === requestId) {
        inFlightRef.current = null;
        setIsLoading(false);
      }
    }
  }, []);

  return (
    <ErrorBoundary name="movers-session-board">
      <div className="flex flex-col gap-3">
        {error && <div className="text-red-500 text-sm px-1">{error}</div>}
        <div className={isLoading ? 'opacity-60 pointer-events-none' : ''}>
          <MoversBoard
            controls={
              <MenuList compact currentItem={session} items={SESSIONS} onClick={loadSession} />
            }
            gainersLabel="Top Gainers"
            layout="split"
            losersLabel="Top Losers"
            maxPerSection={16}
            movers={movers}
            title=""
            viewMoreHref=""
          />
        </div>
      </div>
    </ErrorBoundary>
  );
}
